export const ACTIVE_STATUSES = new Set(['queued', 'running']);
export const TERMINAL_STATUSES = new Set(['succeeded', 'failed']);

export const FILTERS = [
  ['all', 'All'],
  ['high_priority', 'High priority'],
  ['retired_term_still_used', 'Retired use'],
  ['taxonomy_value_leak', 'Taxonomy leaks'],
  ['card_tag_structured_duplicate', 'Tag boundaries'],
  ['non_normalized_term', 'Bad values'],
  ['label_contains_hash', 'Bad labels'],
  ['missing_facet', 'Ungrouped'],
  ['pending_term_suggestion', 'Suggestions'],
  ['approved_term_unused', 'Unused'],
  ['alias_candidate', 'Aliases'],
];

export const EXPORTS = {
  review_queue: {
    label: 'Librarian proposals',
    fields: ['queue_id', 'priority', 'review_type', 'family', 'tag', 'raw_value', 'canonical_tag', 'candidate_tag', 'issue_type', 'issue_subtype', 'all_issue_types', 'issue_count', 'reason', 'confidence', 'usage', 'examples', 'source_table', 'source_id', 'place_master_id', 'card_key', 'recommended_action', 'legacy_target_ids'],
  },
  inventory: {
    label: 'Inventory',
    fields: ['family', 'normalized_tag', 'label', 'vocabulary_status', 'legacy_status', 'total_usage', 'place_usage', 'place_sector_usage', 'place_category_type_usage', 'working_card_usage', 'card_sector_usage', 'card_category_usage', 'card_attribute_usage', 'version_usage', 'published_usage', 'published_category_usage', 'published_attribute_usage', 'usage_sources', 'raw_values', 'examples', 'issue_flags'],
  },
  issues: {
    label: 'Issues',
    fields: ['issue_type', 'issue_subtype', 'family', 'tag', 'raw_value', 'source_table', 'source_id', 'place_master_id', 'card_key', 'detail'],
  },
  alias_candidates: {
    label: 'Alias candidates',
    fields: ['family', 'suggested_canonical', 'candidate_tag', 'reason', 'confidence', 'canonical_usage', 'candidate_usage', 'candidate_examples', 'canonical_examples', 'notes'],
  },
};

export const LIBRARIAN_FAMILIES = [
  'use_case_tag',
  'category',
  'sector',
  'category_type',
  'utility_kind',
  'booking_logic',
  'weather_logic',
  'parking_access',
  'budget',
  'seasonality',
  'practical_use',
  'fact_warning_label',
];

export function humanize(value) {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

export function normalizeValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function canonicalIssueType(value) {
  const normalized = normalizeValue(value);
  if (['location_leak_term', 'taxonomy_value_leak'].includes(normalized)) return 'taxonomy_value_leak';
  return normalized;
}

export function isTaxonomyValueLeak(row) {
  return canonicalIssueType(row?.issue_type) === 'taxonomy_value_leak';
}

export function rowIssueTypes(row) {
  const values = String(row?.all_issue_types || row?.issue_type || '')
    .split('|')
    .map(canonicalIssueType)
    .filter(Boolean);
  if (!values.length && row?.review_type) values.push(canonicalIssueType(row.review_type));
  return Array.from(new Set(values));
}

function stripLegacyNumericPrefix(value) {
  const tokens = normalizeValue(value).split('_').filter(Boolean);
  if (tokens.length < 2) return '';
  if (/^\d{1,2}$/.test(tokens[0])) return tokens.slice(1).join('_');
  return '';
}

export function legacyNumericTargetForRow(row) {
  const candidates = [
    row?.canonical_tag,
    row?.tag,
    row?.raw_value,
    row?.candidate_tag,
    ...(String(row?.queue_id || '').split(/[:|,\s]+/)),
  ];
  for (const value of candidates) {
    const target = stripLegacyNumericPrefix(value);
    if (target) return target;
  }
  return '';
}

export function suggestedCanonicalForRow(row) {
  const canonicalTarget = legacyNumericTargetForRow({ tag: row?.canonical_tag });
  if (canonicalTarget) return canonicalTarget;
  if (row?.canonical_tag) return row.canonical_tag;
  const issueType = canonicalIssueType(row?.issue_type);
  if (
    [
      'retired_term_still_used',
      'used_not_in_approved_vocabulary',
      'non_normalized_term',
      'non_normalized_usage',
      'raw_values_collapse_to_same_tag',
    ].includes(issueType)
  ) {
    return legacyNumericTargetForRow(row);
  }
  return '';
}

function hasSingleSourceId(row) {
  return Boolean(row?.source_id) && !String(row.source_id).includes('|');
}

const LEGACY_REWRITE_SAFE_ISSUES = new Set([
  'non_normalized_term',
  'non_normalized_usage',
  'raw_values_collapse_to_same_tag',
  'used_not_in_approved_vocabulary',
  'retired_term_still_used',
]);

const NORMALIZE_VALUE_SAFE_ISSUES = new Set([
  'non_normalized_term',
  'non_normalized_usage',
  'raw_values_collapse_to_same_tag',
]);

const CLEAN_LABEL_SAFE_ISSUES = new Set(['label_contains_hash']);

function groupedIssueTypes(row) {
  const values = rowIssueTypes(row);
  return values.length ? values : [canonicalIssueType(row?.issue_type)].filter(Boolean);
}

function issuesAreWithin(row, allowed) {
  const issueTypes = groupedIssueTypes(row);
  return Boolean(issueTypes.length) && issueTypes.every(issueType => allowed.has(issueType));
}

export function safeBulkActionForRow(row) {
  if (row?.review_type === 'alias_candidate') return '';
  const issueType = canonicalIssueType(row?.issue_type);
  if (legacyNumericTargetForRow(row) && issuesAreWithin(row, LEGACY_REWRITE_SAFE_ISSUES)) return 'apply_legacy_target';
  if (
    issueType === 'non_normalized_term'
    && row?.source_table === 'vocabulary_terms'
    && hasSingleSourceId(row)
    && issuesAreWithin(row, NORMALIZE_VALUE_SAFE_ISSUES)
  ) return 'normalize_value';
  if (
    ['raw_values_collapse_to_same_tag', 'non_normalized_usage'].includes(issueType)
    && row?.raw_value
    && !String(row.raw_value).includes('|')
    && issuesAreWithin(row, NORMALIZE_VALUE_SAFE_ISSUES)
  ) return 'normalize_value';
  if (
    issueType === 'label_contains_hash'
    && row?.source_table === 'vocabulary_terms'
    && hasSingleSourceId(row)
    && issuesAreWithin(row, CLEAN_LABEL_SAFE_ISSUES)
  ) return 'clean_label';
  return '';
}

export function recommendationForRow(row) {
  const issueType = canonicalIssueType(row?.issue_type);
  const action = normalizeValue(row?.recommended_action);
  const target = suggestedCanonicalForRow(row);
  const bulkAction = safeBulkActionForRow(row);
  const family = familyLabel(row?.family);
  const tag = row?.tag || row?.raw_value || 'term';
  const rawValue = row?.raw_value && normalizeValue(row.raw_value) !== normalizeValue(tag) ? row.raw_value : '';
  const issueTypes = rowIssueTypes(row);
  const groupedPrefix = issueTypes.length > 1 ? `${issueTypes.length} findings on ${tag}: ` : '';

  if (bulkAction === 'apply_legacy_target') {
    const legacyTarget = legacyNumericTargetForRow(row);
    return {
      title: `Rewrite ${tag} to ${legacyTarget}`,
      detail: `Mechanical ${family} cleanup from a legacy numbered/bucket value to its canonical term.`,
      tone: 'safe',
      bulkAction,
    };
  }
  if (bulkAction === 'normalize_value') {
    return {
      title: `Normalize ${rawValue || tag} to ${tag}`,
      detail: `Mechanical ${family} formatting cleanup only; meaning is unchanged.`,
      tone: 'safe',
      bulkAction,
    };
  }
  if (bulkAction === 'clean_label') {
    return {
      title: `Clean ${tag} display label`,
      detail: `Remove invalid label characters from this ${family} term without changing the stored value.`,
      tone: 'safe',
      bulkAction,
    };
  }
  if (bulkAction === 'retire_boundary_duplicate') {
    return {
      title: `Retire duplicate Card Tag ${tag}`,
      detail: `This Card Tag belongs in ${familyLabel(row?.issue_subtype || '') || 'a structured family'}, not Card Tags.`,
      tone: 'safe',
      bulkAction,
    };
  }
  if (row?.review_type === 'alias_candidate') {
    return {
      title: action === 'reject_related_not_alias' ? `Reject ${row?.candidate_tag || tag} as an alias` : `Compare ${row?.candidate_tag || tag} with ${target || row?.canonical_tag || 'canonical term'}`,
      detail: target ? `Confirm whether ${row?.candidate_tag || tag} is truly the same ${family} concept as ${target}.` : `Alias decisions need human confirmation for this ${family} term.`,
      tone: action === 'reject_related_not_alias' ? 'safe' : 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'taxonomy_value_leak') {
    const subtype = normalizeValue(row?.issue_subtype);
    return {
      title: target ? `Rewrite ${tag} leak to ${target}` : `Rewrite or retire ${tag}`,
      detail: `${groupedPrefix}${family} contains source-specific ${subtype || 'taxonomy'} text. Choose a universal value, move it to the right field, or retire it.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'card_tag_structured_duplicate') {
    const structuredFamily = row?.issue_subtype ? familyLabel(row.issue_subtype) : 'a structured field';
    return {
      title: `Move ${tag} out of Card Tags`,
      detail: `${groupedPrefix}${tag} duplicates ${structuredFamily}. Remove it from Card Tags after the structured value is covered.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'retired_term_still_used') {
    return {
      title: target ? `Replace retired ${tag} with ${target}` : `Replace retired ${tag}`,
      detail: `${groupedPrefix}${family} still uses a retired term. Merge to an approved value or remove the current assignment.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'non_normalized_term') {
    return {
      title: `Normalize library value ${rawValue || tag}`,
      detail: `${groupedPrefix}Stored ${family} value should be lowercase snake_case as ${tag}; clear any remaining grouped issues after normalization.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'label_contains_hash') {
    return {
      title: `Clean label for ${tag}`,
      detail: `${groupedPrefix}${family} display label contains '#'. Remove the public-label noise without changing meaning.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (['raw_values_collapse_to_same_tag', 'non_normalized_usage'].includes(issueType)) {
    return {
      title: `Collapse stored spellings to ${tag}`,
      detail: `${groupedPrefix}${rawValue || 'Multiple raw values'} normalize to the same ${family} term. Rewrite current rows through the taxonomy action.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'missing_facet') {
    return {
      title: `Choose ${family} facet for ${tag}`,
      detail: `${groupedPrefix}Assign an approved family-scoped facet or propose a new one if this term does not fit the existing groups.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'approved_term_unused') {
    return {
      title: `Keep or retire unused ${tag}`,
      detail: `${groupedPrefix}${family} term is approved but currently unused. Keep if it is intentional scaffolding; retire if stale.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (issueType === 'used_not_in_approved_vocabulary' || issueType === 'pending_term_suggestion') {
    return {
      title: `Approve, merge, or retire ${tag}`,
      detail: `${groupedPrefix}${family} is growable but governed. Approve only if this is a reusable value; otherwise merge to an approved term or retire it.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (action === 'split_guest_vs_local') {
    return {
      title: `Classify services lane for ${tag}`,
      detail: `${family} services split is place-level work: guest-practical goes to Companion, local services go to directory.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  if (action === 'migrate_to_retrieval_lane') {
    return {
      title: `Move ${tag} to retrieval lane`,
      detail: `${family} value belongs in retrieval lane/uses, not ordinary attributes.`,
      tone: 'review',
      bulkAction: '',
    };
  }
  return {
    title: row?.recommended_action ? `${humanize(row.recommended_action)}: ${tag}` : `Review ${tag}`,
    detail: `${groupedPrefix || ''}${family} needs grouped human review.`,
    tone: 'review',
    bulkAction: '',
  };
}

export function familyLabel(value) {
  const normalized = value || 'use_case_tag';
  const labels = {
    use_case_tag: 'Card Tag',
    category: 'Card Category',
    sector: 'Place Sector',
    category_type: 'Place Category',
    utility_kind: 'Utility Kind',
    booking_logic: 'Booking Logic',
    weather_logic: 'Weather Logic',
    parking_access: 'Arrival & Parking',
    budget: 'Budget',
    seasonality: 'Seasonality',
    practical_use: 'Practical Use',
    fact_warning_label: 'Fact Warning Label',
  };
  return labels[normalized] || humanize(normalized);
}

function csvValue(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadFile(filename, body, type) {
  const url = URL.createObjectURL(new Blob([body], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadCsv(name, rows, fields) {
  const body = [fields.map(csvValue).join(','), ...rows.map(row => fields.map(field => csvValue(row[field])).join(','))].join('\n');
  downloadFile(
    `taxonomy_librarian_${name}_${new Date().toISOString().slice(0, 10)}.csv`,
    `${body}\n`,
    'text/csv;charset=utf-8',
  );
}

export function downloadJson(result) {
  downloadFile(
    `taxonomy_librarian_report_${new Date().toISOString().slice(0, 10)}.json`,
    `${JSON.stringify(result, null, 2)}\n`,
    'application/json;charset=utf-8',
  );
}

function rowContext(row) {
  const place = row.place_master_id || row.examples || row.source_id || '';
  if (place && row.card_key) return `${place}:${row.card_key}`;
  return place;
}

function joinUnique(values, limit = 5, separator = '; ') {
  const out = [];
  const seen = new Set();
  values.forEach(value => {
    String(value || '').split(separator).forEach(part => {
      const clean = part.trim();
      if (!clean || seen.has(clean) || out.length >= limit) return;
      out.push(clean);
      seen.add(clean);
    });
  });
  return out.join(separator);
}

export function aggregateReviewRows(rows) {
  const grouped = new Map();
  (rows || []).forEach(row => {
    if (row.review_type === 'alias_candidate') {
      grouped.set(row.queue_id, [row]);
      return;
    }
    const key = [
      normalizeValue(row.review_type || 'issue') || 'issue',
      normalizeValue(row.family || 'use_case_tag') || 'use_case_tag',
      normalizeValue(row.tag || ''),
    ].join('::');
    grouped.set(key, [...(grouped.get(key) || []), row]);
  });

  return Array.from(grouped.values()).map(items => {
    if (items.length === 1) {
      const item = { ...items[0] };
      item.canonical_tag = suggestedCanonicalForRow(item);
      item.usage = item.usage || '1';
      item.examples = item.examples || rowContext(item);
      item.all_issue_types = item.all_issue_types || item.issue_type || '';
      item.issue_count = item.issue_count || '1';
      return item;
    }
    const first = { ...items[0] };
    first.canonical_tag = suggestedCanonicalForRow(first);
    const unique = field => Array.from(new Set(items.map(item => item[field]).filter(Boolean))).sort();
    const sourceTables = unique('source_table');
    const sourceIds = unique('source_id');
    const placeIds = unique('place_master_id');
    const cardKeys = unique('card_key');
    const rawValues = unique('raw_value');
    const issueTypes = Array.from(new Set(items.flatMap(rowIssueTypes))).sort();
    const issueSubtypes = unique('issue_subtype');
    const legacyTargetIds = unique('legacy_target_ids');
    first.queue_id = [
      'issue',
      first.family || 'use_case_tag',
      first.tag,
    ].filter(Boolean).join(':');
    first.all_issue_types = issueTypes.join('|');
    first.issue_count = String(items.reduce((sum, item) => sum + Number(item.issue_count || 1), 0));
    if (issueSubtypes.length > 1) first.issue_subtype = issueSubtypes.join('|');
    first.legacy_target_ids = legacyTargetIds.join('|');
    first.usage = String(items.length);
    first.examples = joinUnique(items.map(rowContext), 5);
    first.source_table = sourceTables.join('|');
    first.source_id = sourceIds.length === 1 ? sourceIds[0] : '';
    first.raw_value = rawValues.slice(0, 5).join('|');
    first.place_master_id = placeIds.length === 1 ? placeIds[0] : '';
    first.card_key = cardKeys.length === 1 ? cardKeys[0] : '';
    first.reason = `${items.length} findings${issueTypes.length ? ` (${issueTypes.map(humanize).join(', ')})` : ''}. ${first.reason || ''}`.trim();
    return first;
  });
}

export function jobStatusLabel(job) {
  if (!job) return 'Not run';
  if (job.status === 'succeeded') return 'Ready';
  return humanize(job.status);
}
